import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import Moment from 'moment';
import Services, { API_ROOT } from '../service/Services';
import Spinner from '../components/Spinner';
import { Link, useNavigate } from 'react-router-dom';
import { exportToExcel } from '../pages/excelGenerate';
import { exportToPDF } from '../pages/pdfGenerate';
import {
  HiOutlineEye,
  HiOutlinePencil,
  HiOutlineX,
  HiOutlineDocumentDownload,
  HiOutlineInformationCircle,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineMail,
  HiOutlineMailOpen,
  HiOutlineDocumentText,
  HiOutlineShoppingCart,
  HiOutlineTruck,
  HiOutlineRefresh,
  HiOutlineAdjustments
} from 'react-icons/hi';
import { createPortal } from 'react-dom';

// Tooltip component using portal to render outside overflow containers
function Tooltip({ children, content }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef();

  const showTooltip = (e) => {
    const rect = e.target.getBoundingClientRect();
    setCoords({ top: rect.top + window.scrollY - 8, left: rect.left + window.scrollX });
    setVisible(true);
  };

  const hideTooltip = () => {
    setVisible(false);
  };

  return (
    <>
      <div
        ref={ref}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className="p-1 rounded-full bg-gray-300 text-black flex items-center justify-center cursor-pointer"
      >
        {children}
      </div>
      {visible &&
        createPortal(
          <div
            style={{
              position: 'absolute',
              top: coords.top,
              left: coords.left,
              transform: 'translateY(-100%)',
              zIndex: 9999,
              width: 280,
              backgroundColor: 'white',
              border: '1px solid #ccc',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              padding: 12,
              fontSize: 12,
              borderRadius: 6,
              pointerEvents: 'none',
              whiteSpace: 'normal',
            }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
}

export default function InvoiceListing() {
  const navigation = useNavigate();
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [invoiceList, setInvoiceList] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [companyDetails, setCompanyDetails] = useState([]);
  const [filter, setFilter] = useState({
    customer: '',
    sales: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    quotationId: '',
    transactionType: '',
    isApproved: '',
    mailSent: ''
  });
  const [customerOptions, setCustomerOptions] = useState([]);
  const [salesOptions, setSalesOptions] = useState([]);
  const [emailModal, setEmailModal] = useState({ visible: false, data: null });
  const [emailForm, setEmailForm] = useState({
    recipients: '',
    subject: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    getCompany();
  }, []);

  useEffect(() => {
    getInvoiceList(currentPage, search);
  }, []);

  // Fetch customers and sales for filter dropdowns
  useEffect(() => {
    axios.get(`${API_ROOT}/customer`, {
      headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(res => {
      setCustomerOptions(res.data?.data || []);
    });
    axios.get(`${API_ROOT}/sales`, {
      headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(res => {
      setSalesOptions(res.data?.data || []);
    });
  }, []);

  const handleExport = (modifiedData) => {
    exportToExcel(modifiedData, modifiedData?.companyDetails, setLoading, navigation);
  };

  const getCompany = async () => {
    try {
      const response = await axios.get(`${API_ROOT}/company`, {
        headers: { authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (response.data) {
        setCompanyDetails(response.data);
      } else {
        setCompanyDetails([]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Fetch invoice list with filters
  const getInvoiceList = async (page = 1, name = '', filterObj = filter) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: perPage,
        name,
        ...filterObj
      };
      const response = await axios.get(`${API_ROOT}/delivery-note`, {
        headers: {
          authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        params,
      });

      if (response.data.success) {
        const normalizedData = (response.data.data || []).map((dn) => {
          const items = dn.items || [];
          const totalQty = items.reduce((sum, it) => sum + Number(it.quantity || 0), 0);
          return {
            ...dn,
            customer: dn.customerId || {
              _id: 'N/A',
              name: 'Unknown',
              address: 'N/A',
              gstno: 'N/A',
              isActive: false,
            },
            sales: dn.salesPersonId || {},
            totalQty,
          };
        });
        setInvoiceList(normalizedData);
        setTotalRows(response.data.totalItems || 0);
        setCurrentPage(response.data.currentPage || 1);
      } else {
        setInvoiceList([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const cancelInvoice = async (id) => {
    if (window.confirm('Do you really want to cancel?')) {
      try {
        setLoading(true);
        await Services.Common.invoice_update(id);
        await getInvoiceList(currentPage, search);
      } catch (error) {
        console.error('Error cancelling invoice:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Send email function
  const sendEmail = async () => {
    if (!emailForm.recipients.trim()) {
      alert('Please enter recipient email addresses');
      return;
    }

    try {
      setSendingEmail(true);
      const recipients = emailForm.recipients.split(',').map(email => email.trim());

      const response = await axios.post(
        `${API_ROOT}/delivery-note/${emailModal.data._id}/send-email`,
        {
          recipients,
          subject: emailForm.subject,
          message: emailForm.message
        },
        {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );

      if (response.data.success) {
        alert('Email sent successfully!');
        setEmailModal({ visible: false, data: null });
        setEmailForm({ recipients: '', subject: '', message: '' });
        // Refresh the list to update mail status
        getInvoiceList(currentPage, search, filter);
      } else {
        alert('Failed to send email: ' + response.data.message);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Error sending email: ' + (error.response?.data?.message || error.message));
    } finally {
      setSendingEmail(false);
    }
  };

  // Open email modal
  const openEmailModal = (row) => {
    const customer = row.customer;
    const defaultSubject = `Delivery Note ${row.noteNumber} - ${customer?.name || 'Customer'}`;
    const defaultMessage = `Dear ${customer?.name || 'Customer'},\n\nPlease find attached the delivery note ${row.noteNumber}.\n\nBest regards,\nYour Company Team`;

    setEmailForm({
      recipients: customer?.email || '',
      subject: defaultSubject,
      message: defaultMessage
    });
    setEmailModal({ visible: true, data: row });
  };


  const handlePageChange = (page) => {
    getInvoiceList(page, search);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    getInvoiceList(1, value); // Reset to page 1 on search
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
  };

  // Apply filter button
  const applyFilter = () => {
    getInvoiceList(1, search, filter);
  };

  // Reset filter button
  const resetFilter = () => {
    setFilter({
      customer: '',
      sales: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      quotationId: '',
      transactionType: '',
      isApproved: '',
      mailSent: ''
    });
    getInvoiceList(1, search, {
      customer: '',
      sales: '',
      status: '',
      dateFrom: '',
      dateTo: '',
      quotationId: '',
      transactionType: '',
      isApproved: '',
      mailSent: ''
    });
  };

  // Enhanced columns with all important fields
  const columns = [
    {
      name: 'Note No.',
      selector: (row) => row?.noteNumber || 'N/A',
      width: '10%',
      style: { fontWeight: 'bold', textAlign: 'center', fontSize: '13px', justifyContent: 'center' },
      center: true,
      wrap: true,
    },
    {
      name: 'Date',
      selector: (row) => Moment(row?.createdAt).format('DD-MM-YYYY'),
      width: '8%',
      style: { textAlign: 'center', fontSize: '13px', justifyContent: 'center' },
      center: true,
      wrap: true,
    },
    {
      name: 'Customer',
      selector: (row) => row?.customer?.name || 'Unknown',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <span className="truncate max-w-[100px]">{row?.customer?.name || 'Unknown'}</span>
          <Tooltip
            content={
              <>
                <h4 className="font-semibold mb-2">Customer Details</h4>
                <p><strong>Name:</strong> {row.customer?.name || 'N/A'}</p>
                <p><strong>Address:</strong> {row.customer?.address || 'N/A'}</p>
                <p><strong>GST No:</strong> {row.customer?.gstno || 'N/A'}</p>
              </>
            }
          >
            <HiOutlineInformationCircle size={16} />
          </Tooltip>
        </div>
      ),
      width: '12%',
      style: { alignItems: 'center' }
    },
    {
      name: 'Sales',
      selector: (row) => row?.sales?.name || 'Unknown',
      cell: (row) => {
        const sales = row?.sales || {};
        return (
          <div className="flex items-center gap-2 py-1">
            <span className="truncate max-w-[100px]">{sales.name || 'Unknown'}</span>
            <Tooltip
              content={
                <>
                  <h4 className="font-semibold mb-2">Sales Person Details</h4>
                  <div className="mb-1"><strong>Name:</strong> {sales.name || 'N/A'}</div>
                  <div className="mb-1"><strong>Email:</strong> {sales.email || 'N/A'}</div>
                  <div className="mb-1"><strong>Phone:</strong> {sales.phoneNo || 'N/A'}</div>
                  <div className="mb-1"><strong>Designation:</strong> {sales.designation || 'N/A'}</div>
                  <div className="mb-1"><strong>Department:</strong> {sales.department || 'N/A'}</div>
                </>
              }
            >
              <HiOutlineInformationCircle size={16} />
            </Tooltip>
          </div>
        );
      },
      width: '12%',
      style: { alignItems: 'center', paddingTop: 6, paddingBottom: 6 }
    },
    {
      name: 'Transaction Type',
      cell: (row) => {
        const type = row.transactionType || 'N/A';
        const getTypeIcon = (type) => {
          switch (type.toLowerCase()) {
            case 'purchase': return <HiOutlineShoppingCart className="text-blue-600" size={16} />;
            case 'sale': return <HiOutlineTruck className="text-green-600" size={16} />;
            case 'return': return <HiOutlineRefresh className="text-orange-600" size={16} />;
            case 'adjustment': return <HiOutlineAdjustments className="text-purple-600" size={16} />;
            default: return <HiOutlineDocumentText className="text-gray-600" size={16} />;
          }
        };

        return (
          <div className="flex items-center gap-2 justify-center">
            {getTypeIcon(type)}
            <span className="text-xs font-medium capitalize">{type}</span>
          </div>
        );
      },
      width: '10%',
      center: true,
    },
    {
      name: 'Items',
      cell: (row) => {
        const count = row.items?.length || 0;
        const totalQty = row.totalQty || 0;
        return (
          <div className="flex items-center gap-2">
            <span className="text-xs">{count} items</span>
            <span className="text-xs font-semibold">Qty: {totalQty}</span>
            <Tooltip
              content={
                <>
                  <h4 className="font-semibold mb-2">Items</h4>
                  <ul className="list-disc ml-4">
                    {(row.items || []).map((it) => (
                      <li key={it._id}>
                        {(it.itemId?.itemName) || 'Item'} — Qty: {it.quantity}
                      </li>
                    ))}
                  </ul>
                </>
              }
            >
              <HiOutlineEye size={16} />
            </Tooltip>
          </div>
        );
      },
      width: '12%'
    },
    {
      name: 'Approval Status',
      cell: (row) => {
        const isApproved = row.isApproved;
        const approvedBy = row.approvedBy;
        const approvedAt = row.approvedAt;

        if (isApproved) {
          return (
            <div className="flex items-center gap-2 justify-center">
              <HiOutlineCheckCircle className="text-green-600" size={16} />
              <div className="text-center">
                <div className="text-xs font-semibold text-green-700">Approved</div>
                {approvedAt && (
                  <div className="text-xs text-green-600">
                    {Moment(approvedAt).format('DD-MM-YY')}
                  </div>
                )}
              </div>
            </div>
          );
        } else {
          return (
            <div className="flex items-center gap-2 justify-center">
              <HiOutlineClock className="text-yellow-600" size={16} />
              <div className="text-xs font-semibold text-yellow-700">Pending</div>
            </div>
          );
        }
      },
      width: '10%',
      center: true,
    },
    {
      name: 'Mail Status',
      cell: (row) => {
        // This would need to be implemented based on your email tracking system
        const mailSent = row.mailSent || false;
        const mailSentAt = row.mailSentAt;

        if (mailSent) {
          return (
            <div className="flex items-center gap-2 justify-center">
              <HiOutlineMailOpen className="text-green-600" size={16} />
              <div className="text-center">
                <div className="text-xs font-semibold text-green-700">Sent</div>
                {mailSentAt && (
                  <div className="text-xs text-green-600">
                    {Moment(mailSentAt).format('DD-MM-YY')}
                  </div>
                )}
              </div>
            </div>
          );
        } else {
          return (
            <div className="flex items-center gap-2 justify-center">
              <HiOutlineMail className="text-gray-500" size={16} />
              <div className="text-xs font-semibold text-gray-600">Not Sent</div>
            </div>
          );
        }
      },
      width: '10%',
      center: true,
    },
    {
      name: 'Status',
      cell: (row) => {
        const status = row.status || 'Draft';
        const getStatusStyle = (status) => {
          switch (status.toLowerCase()) {
            case 'submitted':
              return 'border-blue-500 text-blue-600 bg-blue-50';
            case 'pendingapproval':
              return 'border-yellow-500 text-yellow-600 bg-yellow-50';
            case 'cancelled':
              return 'border-red-500 text-red-600 bg-red-50';
            case 'draft':
            default:
              return 'border-gray-400 text-gray-700 bg-gray-100';
          }
        };

        return (
          <span className={`rounded px-2 py-1 text-xs font-semibold border-2 min-w-[80px] text-center block ${getStatusStyle(status)}`}>
            {status}
          </span>
        );
      },
      width: '8%',
      center: true,
    },
    {
      name: 'Remarks',
      selector: (row) => row?.remarks || '-',
      width: '10%',
      wrap: true
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-2 justify-center">
         <button
  onClick={() => {
    if (row.status === "Submitted") {
      window.open(`/delivery-note-approval/${row._id}`, "_blank");
    }
  }}
  disabled={row.status !== "Submitted"}
  className={`p-1 rounded ${
    row.status === "Submitted"
      ? "text-blue-600 hover:text-blue-800 hover:bg-blue-50"
      : "text-gray-400 cursor-not-allowed"
  }`}
  title={
    row.status === "Submitted"
      ? "View Details"
      : "Available only for Submitted status"
  }
>
  <HiOutlineEye size={16} />
</button>

          {row.status === 'Draft' && (
            <button
              onClick={() => navigation(`/invoice/edit/${row._id}`)}
              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
              title="Edit"
            >
              <HiOutlinePencil size={16} />
            </button>
          )}
          <button
            onClick={() => openEmailModal(row)}
            className={`p-1 rounded ${row.mailSent ? 'text-green-600 hover:text-green-800 hover:bg-green-50' : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'}`}
            title={row.mailSent ? 'Resend Email' : 'Send Email'}
          >
            <HiOutlineMail size={16} />
          </button>
          {row.status !== 'Cancelled' && (
            <button
              onClick={() => cancelInvoice(row._id)}
              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
              title="Cancel"
            >
              <HiOutlineX size={16} />
            </button>
          )}
        </div>
      ),
      width: '10%',
      center: true,
    },
  ];

  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200 flex flex-col flex-1 shadow-lg">
      <div className="mb-6 flex flex-wrap gap-4 items-end bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4">
        <div>
          <label className="block text-xs font-semibold mb-1 text-blue-900">Customer</label>
          <select
            name="customer"
            value={filter.customer}
            onChange={handleFilterChange}
            className="border border-blue-300 rounded px-2 py-1 text-sm w-44 focus:ring focus:ring-blue-200"
          >
            <option value="">All</option>
            {customerOptions.map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-blue-900">Sales</label>
          <select
            name="sales"
            value={filter.sales}
            onChange={handleFilterChange}
            className="border border-blue-300 rounded px-2 py-1 text-sm w-44 focus:ring focus:ring-blue-200"
          >
            <option value="">All</option>
            {salesOptions.map(s => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-blue-900">Status</label>
          <select
            name="status"
            value={filter.status}
            onChange={handleFilterChange}
            className="border border-blue-300 rounded px-2 py-1 text-sm w-32 focus:ring focus:ring-blue-200"
          >
            <option value="">All</option>
            <option value="Draft">Draft</option>
            <option value="PendingApproval">Pending Approval</option>
            <option value="Submitted">Submitted</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-blue-900">Transaction Type</label>
          <select
            name="transactionType"
            value={filter.transactionType}
            onChange={handleFilterChange}
            className="border border-blue-300 rounded px-2 py-1 text-sm w-32 focus:ring focus:ring-blue-200"
          >
            <option value="">All</option>
            <option value="Purchase">Purchase</option>
            <option value="Sale">Sale</option>
            <option value="Return">Return</option>
            <option value="Adjustment">Adjustment</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-blue-900">Approval</label>
          <select
            name="isApproved"
            value={filter.isApproved}
            onChange={handleFilterChange}
            className="border border-blue-300 rounded px-2 py-1 text-sm w-32 focus:ring focus:ring-blue-200"
          >
            <option value="">All</option>
            <option value="true">Approved</option>
            <option value="false">Pending</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-blue-900">Mail Status</label>
          <select
            name="mailSent"
            value={filter.mailSent}
            onChange={handleFilterChange}
            className="border border-blue-300 rounded px-2 py-1 text-sm w-32 focus:ring focus:ring-blue-200"
          >
            <option value="">All</option>
            <option value="true">Sent</option>
            <option value="false">Not Sent</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-blue-900">From</label>
          <input
            type="date"
            name="dateFrom"
            value={filter.dateFrom}
            onChange={handleFilterChange}
            className="border border-blue-300 rounded px-2 py-1 text-sm focus:ring focus:ring-blue-200"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-blue-900">To</label>
          <input
            type="date"
            name="dateTo"
            value={filter.dateTo}
            onChange={handleFilterChange}
            className="border border-blue-300 rounded px-2 py-1 text-sm focus:ring focus:ring-blue-200"
          />
        </div>
        <button
          onClick={applyFilter}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-sm font-semibold ml-2 shadow"
        >
          Apply
        </button>
        <button
          onClick={resetFilter}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-1.5 rounded text-sm font-semibold shadow"
        >
          Reset
        </button>
      </div>

      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-gray-800">Delivery Notes</h2>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Total: {totalRows}</span>
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded">Approved: {invoiceList.filter(item => item.isApproved).length}</span>
            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">Pending: {invoiceList.filter(item => !item.isApproved).length}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {/* <button
            onClick={() => handleExport(invoiceList)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2"
          >
            <HiOutlineDocumentDownload size={16} />
            Export Excel
          </button> */}
          {/* <button
            onClick={() => exportToPDF(invoiceList, companyDetails, setLoading, navigation)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm font-semibold flex items-center gap-2"
          >
            <HiOutlineDocumentDownload size={16} />
            Export PDF
          </button> */}
        </div>
      </div>

      <div className="mt-2 w-full flex-1 text-xs rounded-xl overflow-hidden shadow">
        {loading && <Spinner />}
        <DataTable
          columns={columns}
          data={invoiceList}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          paginationPerPage={perPage}
          fixedHeader
          fixedHeaderScrollHeight="400px"
          highlightOnHover
          selectableRows
          dense
          customStyles={{
            table: {
              style: {
                width: '100%',
                minWidth: '100%',
                overflowX: 'auto',
                background: 'white'
              }
            },
            headCells: {
              style: {
                background: 'linear-gradient(90deg, #e0e7ff 0%, #f1f5f9 100%)',
                color: '#334155',
                fontWeight: 700,
                fontSize: '13px',
                borderBottom: '2px solid #e2e8f0',
                textAlign: 'center',
                letterSpacing: '0.5px'
              }
            },
            rows: {
              style: {
                minHeight: '45px',
                fontSize: '12px',
                textAlign: 'center',
                transition: 'background 0.2s',
                background: 'white'
              },
              highlightOnHoverStyle: {
                backgroundColor: '#f0f9ff',
                borderBottomColor: '#e0e7ff',
                outline: 'none'
              }
            }
          }}
        />
      </div>

      {/* Email Modal */}
      {emailModal.visible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Send Email</h3>
              <button
                onClick={() => setEmailModal({ visible: false, data: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <HiOutlineX size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipients (comma-separated)
                </label>
                <input
                  type="email"
                  value={emailForm.recipients}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, recipients: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring focus:ring-blue-200 focus:border-blue-500"
                  placeholder="email1@example.com, email2@example.com"
                  multiple
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring focus:ring-blue-200 focus:border-blue-500"
                  placeholder="Email subject"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={emailForm.message}
                  onChange={(e) => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                  rows={6}
                  className="w-full border border-gray-300 rounded px-3 py-2 focus:ring focus:ring-blue-200 focus:border-blue-500"
                  placeholder="Email message"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEmailModal({ visible: false, data: null })}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={sendEmail}
                disabled={sendingEmail}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {sendingEmail ? (
                  <>
                    <Spinner />
                    Sending...
                  </>
                ) : (
                  <>
                    <HiOutlineMail size={16} />
                    Send Email
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
